package one.healthium.profile.ui.food.components

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.text.BasicText
import androidx.compose.foundation.text.TextAutoSize
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import loco.LOG_FOOD_FOOD_DETAILS_CALORIES_UNIT_LABEL
import loco.LOG_FOOD_FOOD_DETAILS_CARBS_LABEL
import loco.LOG_FOOD_FOOD_DETAILS_FAT_LABEL
import loco.LOG_FOOD_FOOD_DETAILS_FIBER_LABEL
import loco.LOG_FOOD_FOOD_DETAILS_PROTEIN_LABEL
import one.healthium.loco.LString
import one.healthium.loco.resolve
import one.healthium.profile.ui.food.models.AmountUiModel
import one.healthium.profile.ui.food.models.FoodUiModel
import one.healthium.profile.ui.food.models.FoodUiOptions
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.gray_13
import org.jetbrains.compose.resources.stringResource
import kotlin.time.Clock

/**
 * Shows a food details card with name, macros, and calorie count.
 *
 * @param modifier Modifier for the outer container.
 * @param option The food options providing nutrient values.
 * @param title Optional override for the food name display. When null, [option.food.displayName] is used.
 * @param bottomContent Optional slot rendered below the nutrients section, inside the card background,
 *   separated by a [HorizontalDivider]. Intended for additional rows such as log time or amount.
 */
@Composable
fun FoodDetails(
    modifier: Modifier = Modifier,
    option: FoodUiOptions,
    title: String? = null,
    bottomContent: (@Composable () -> Unit)? = null,
) {
    FoodDetailsLayout(
        modifier = modifier,
        title = title ?: option.food.displayName,
        carbsDisplayValue = option.carbohydratesDisplayValue.resolve(),
        proteinDisplayValue = option.proteinDisplayValue.resolve(),
        fatDisplayValue = option.fatDisplayValue.resolve(),
        fiberDisplayValue = option.fiberDisplayValue.resolve(),
        caloriesDisplayValue = option.caloriesDisplayValue,
        bottomContent = bottomContent,
    )
}

/**
 * Shows a food details card with a custom title and pre-computed nutrient display values.
 * Use this when nutrient values are not derived from a single [FoodUiOptions] (e.g. summed totals).
 *
 * @param modifier Modifier for the outer container.
 * @param bottomContent Optional slot rendered below the nutrients section, inside the card background,
 *   separated by a [HorizontalDivider]. Intended for additional rows such as log time or amount.
 */
@Composable
fun FoodDetailsRaw(
    modifier: Modifier = Modifier,
    title: String,
    carbsDisplayValue: LString,
    proteinDisplayValue: LString,
    fatDisplayValue: LString,
    fiberDisplayValue: LString,
    caloriesDisplayValue: Int,
    bottomContent: (@Composable () -> Unit)? = null,
) {
    FoodDetailsLayout(
        modifier = modifier,
        title = title,
        carbsDisplayValue = carbsDisplayValue.resolve(),
        proteinDisplayValue = proteinDisplayValue.resolve(),
        fatDisplayValue = fatDisplayValue.resolve(),
        fiberDisplayValue = fiberDisplayValue.resolve(),
        caloriesDisplayValue = caloriesDisplayValue,
        bottomContent = bottomContent,
    )
}

@Composable
private fun FoodDetailsLayout(
    modifier: Modifier = Modifier,
    title: String,
    carbsDisplayValue: String,
    proteinDisplayValue: String,
    fatDisplayValue: String,
    fiberDisplayValue: String,
    caloriesDisplayValue: Int,
    bottomContent: (@Composable () -> Unit)? = null,
) {
    Column(
        modifier = modifier
            .fillMaxWidth()
            .padding(12.dp)
            .background(gray_13, AppTheme.shapes.small)
    ) {
        Row(modifier = Modifier.padding(horizontal = 12.dp)) {
            Column(
                modifier = Modifier
                    .weight(1f)
                    .padding(vertical = 12.dp),
                verticalArrangement = Arrangement.Top,
            ) {
                Text(
                    text = title,
                    style = AppTheme.textStyles.secondary_18_700,
                    color = AppTheme.colors.text100,
                    minLines = 2,
                    maxLines = 2,
                )
                Spacer(Modifier.height(8.dp))
                FoodDetailRow(
                    label = stringResource(loco.Res.string.LOG_FOOD_FOOD_DETAILS_CARBS_LABEL),
                    value = carbsDisplayValue,
                )
                Spacer(Modifier.height(2.dp))
                FoodDetailRow(
                    label = stringResource(loco.Res.string.LOG_FOOD_FOOD_DETAILS_PROTEIN_LABEL),
                    value = proteinDisplayValue,
                )
                Spacer(Modifier.height(2.dp))
                FoodDetailRow(
                    label = stringResource(loco.Res.string.LOG_FOOD_FOOD_DETAILS_FAT_LABEL),
                    value = fatDisplayValue,
                )
                Spacer(Modifier.height(2.dp))
                FoodDetailRow(
                    label = stringResource(loco.Res.string.LOG_FOOD_FOOD_DETAILS_FIBER_LABEL),
                    value = fiberDisplayValue,
                )
            }
            Column(
                modifier = Modifier
                    .padding(start = 8.dp, top = 12.dp, end = 12.dp)
                    .width(140.dp),
                horizontalAlignment = Alignment.End,
            ) {
                BasicText(
                    text = "$caloriesDisplayValue",
                    style = AppTheme.textStyles.secondary_64_700.merge(
                        color = AppTheme.colors.text100,
                        textAlign = TextAlign.End,
                    ),
                    maxLines = 1,
                    autoSize = TextAutoSize.StepBased(
                        minFontSize = 16.sp,
                        maxFontSize = 64.sp,
                    ),
                )
                Text(
                    text = stringResource(loco.Res.string.LOG_FOOD_FOOD_DETAILS_CALORIES_UNIT_LABEL),
                    style = AppTheme.textStyles.secondary_18_700,
                    color = AppTheme.colors.text100,
                    textAlign = TextAlign.End,
                )
            }
        }
        if (bottomContent != null) {
            HorizontalDivider(
                modifier = Modifier.padding(horizontal = 12.dp),
                thickness = 1.dp,
                color = Color.Black.copy(alpha = 0.1f),
            )
            bottomContent()
        }
    }
}

@Composable
private fun FoodDetailRow(label: String, value: String) {
    FlowRow {
        Text(
            text = label,
            style = AppTheme.textStyles.secondary_18_700,
            color = AppTheme.colors.text100,
        )
        Spacer(Modifier.width(4.dp))
        Text(
            text = value,
            style = AppTheme.textStyles.secondary_18_400,
            color = AppTheme.colors.text100,
        )
    }
}

@Composable
@Preview
private fun FoodDetailsPreview() {
    AppTheme {
        FoodDetails(
            option = FoodUiOptions(
                food = FoodUiModel.preview,
                amount = AmountUiModel.preview,
                date = Clock.System.now()
            )
        )
    }
}
